import { Injectable, signal, computed, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationsService, ChatService } from '../../modules/api';
import { RealtimeService } from '../../core/realtime.service';
import { AuthService } from '../../core/auth.service';

export interface NotificationItem {
  id: string;
  kind: string;
  read: boolean;
  created_at: string;
  payload?: Record<string, any>;
}

@Injectable({ providedIn: 'root' })
export class NotificationUiService {
  private router = inject(Router);
  private notificationService = inject(NotificationsService);
  private chatService = inject(ChatService);
  private realtime = inject(RealtimeService);
  private auth = inject(AuthService);

  readonly notifications = signal<NotificationItem[]>([]);
  readonly loading = signal<boolean>(false);
  readonly unreadCount = computed(() => this.notifications().filter(n => !n.read).length);
  readonly presenceMap = signal<Record<string, boolean>>({});

  constructor() {
    this.realtime.notifications$.subscribe((notification) => {
      if (notification?.name === 'ui.notifications.changed') {
        this.load();
        return;
      }
      this.notifications.update((items) => {
        if (items.some((item) => item.id === notification?.id)) {
          return items;
        }
        return [{ ...notification, read: false }, ...items];
      });
    });
    this.realtime.messages$.subscribe((message) => {
      if (!message?.thread_id) return;
      this.notifications.update((items) => {
        const existing = items.find(
          (item) => item.kind === 'new_message' && item.payload?.['thread_id'] === message.thread_id && !item.read
        );
        if (existing) {
          return items;
        }
        return [{
          id: `realtime-${message.thread_id}-${message.timestamp || Date.now()}`,
          kind: 'new_message',
          read: false,
          created_at: message.timestamp || new Date().toISOString(),
          payload: {
            thread_id: message.thread_id,
            sender_id: message.sender_id,
            text_preview: message.text,
          },
        }, ...items];
      });
    });
    this.realtime.presence$.subscribe((presence) => {
      if (!presence?.user_id) return;
      this.presenceMap.update(map => ({ ...map, [presence.user_id]: !!presence.is_online }));
    });
  }

  load(): void {
    if (!this.auth.isLoggedIn()) {
      this.notifications.set([]);
      this.presenceMap.set({});
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.notificationService.getNotificationsApiNotificationsGet().subscribe({
      next: (items) => {
        this.notifications.set(items);
        this.loading.set(false);
        this.refreshPresenceMap();
      },
      error: () => {
        this.loading.set(false);
      }
    });
  }

  markAllRead(): void {
    this.notificationService.markAllNotificationsAsReadApiNotificationsMarkAllReadPost().subscribe({
      next: () => {
        this.notifications.update(list => list.map(n => ({ ...n, read: true })));
      },
      error: (err: any) => {
        console.error('Failed to mark all notifications as read:', err);
      }
    });
  }

  markRead(notificationId: string): void {
    this.notificationService.markNotificationAsReadApiNotificationsNotificationIdReadPost(notificationId).subscribe({
      next: () => {
        this.notifications.update(list =>
          list.map(n => n.id === notificationId ? { ...n, read: true } : n)
        );
      },
      error: (err: any) => {
        console.error('Failed to mark notification as read:', err);
      }
    });
  }

  open(notification: NotificationItem): void {
    this.markRead(notification.id);
    
    if (notification.kind === 'new_message' && notification.payload?.['thread_id']) {
      this.router.navigate(['/chat'], { queryParams: { thread: notification.payload['thread_id'] } });
      return;
    }
    if (notification.kind === 'moderation_new') {
      this.router.navigate(['/moderation']);
      return;
    }
    if (notification.kind === 'moderation_status' && notification.payload?.['property_id']) {
      this.router.navigate(['/property', notification.payload['property_id']]);
    }
  }

  getTitle(notification: NotificationItem): string {
    switch (notification.kind) {
      case 'new_message':
        return 'Новое сообщение';
      case 'property_view':
        return 'Просмотр вашего объявления';
      case 'property_click':
        return 'Клик по вашему объявлению';
      case 'status_change':
        return 'Изменение статуса объявления';
      case 'moderation_new':
        return 'Новое объявление на модерации';
      case 'moderation_status':
        return 'Статус объявления изменен';
      default:
        return 'Уведомление';
    }
  }

  senderName(notification: NotificationItem): string {
    return (notification?.payload?.['sender_name'] || '').trim();
  }

  isSenderOnline(notification: NotificationItem): boolean {
    const senderId = notification?.payload?.['sender_id'];
    if (!senderId) return false;
    return !!this.presenceMap()[senderId];
  }

  senderStatus(notification: NotificationItem): 'online' | 'offline' {
    return this.isSenderOnline(notification) ? 'online' : 'offline';
  }

  private refreshPresenceMap(): void {
    if (!this.auth.isLoggedIn()) {
      this.presenceMap.set({});
      return;
    }

    this.chatService.getChatThreadsApiChatThreadsGet().subscribe({
      next: (threads: any[]) => {
        const nextMap: Record<string, boolean> = {};
        for (const t of threads || []) {
          const uid = t?.other_user?.id;
          if (uid) nextMap[uid] = !!t?.other_user?.is_online;
        }
        this.presenceMap.set(nextMap);
      },
      error: () => {}
    });
  }
}
